import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'shared/lib/request';
import { api } from 'shared/api/client';
import { errorHandler } from 'shared/feedback/messages';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n } from 'shared/i18n';
import { userKeys } from './keys';

export const useCurrentUser = () => {
  return useQuery({
    queryKey: userKeys.current(),

    queryFn: async () => {
      const responseUser = await request.get(`/registry/users/current-user`);
      const responsePermissions = await request.get(`/registry/users/current-user-permissions`);
      return { user: responseUser.data, permissions: responsePermissions.data };
    },

    meta: { errorHandler },
  });
};

export const useUsersInOrganization = (organizationId: number | string) => {
  return useQuery({
    queryKey: userKeys.inOrganization(organizationId),

    queryFn: async () => {
      const response = await request.get(`/registry/users?attributeId=${organizationId}&size=1000`);
      return response.data._embedded?.users ?? [];
    },

    meta: { errorHandler },
    placeholderData: keepPreviousData,
    enabled: !!organizationId,
  });
};

export const useUsersWithSectionPermission = (sectionSuperId: string, includeSelf = true) => {
  return useQuery({
    queryKey: userKeys.currentPermissions(),

    queryFn: async () => {
      const { data: responseUser } = await request.get(`/registry/users/current-user`);
      const { data: documentPermissions } = await api.GET('/comments/documents/{sectionSuperId}/permissions', {
        params: { path: { sectionSuperId } },
      });
      const { data: users } = await request.get(`/registry/users?size=1000`);
      const usersWithAcces = users._embedded.users.filter((user: { id: number }) => {
        const permission = documentPermissions.userSectionPermissions.find(
          (perm) => perm.sectionSuperId === sectionSuperId && perm.userId === user.id
        )?.permission;
        return (permission === 'COMMENT' || permission === 'WRITE') && (includeSelf || user.id !== responseUser.id);
      });

      return usersWithAcces ?? [];
    },

    meta: { errorHandler },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileData: unknown) => request.put('/registry/users/current-user', profileData),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userKeys.current(),
      });
    },

    onError: ServerErrorHandler,
  });
};

export const useAdminUsers = () => {
  return useQuery({
    queryKey: userKeys.adminUsers(),

    queryFn: async () => {
      const response = await request.get('/registry/users?size=10000').catch((err: { status?: number }) => {
        if (err.status === 404) return null;
        throw err;
      });
      return response?.data?._embedded?.users ?? [];
    },

    meta: { errorHandler },
  });
};

export const useRevokeToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => request.del(`/registry/users/${userId}/revoke-token`, null),

    onError: () => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        title: i18n('admin-users:revoke.error.title'),
        text: i18n('admin-users:revoke.error'),
      });
    },

    onSuccess: () => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        title: i18n('admin-users:revoke.success.title'),
        text: i18n('admin-users:revoke.success'),
      });
      queryClient.invalidateQueries({ queryKey: userKeys.adminUsers() });
    },
  });
};

export const useChangePassword = () =>
  useMutation({
    mutationFn: ({ currentPassword, newPassword1, newPassword2 }: { currentPassword: string; newPassword1: string; newPassword2: string }) =>
      request.patch('/registry/users/current-user/password', { currentPassword, newPassword1, newPassword2 }),

    onError: ServerErrorHandler,
  });
